const MEDIA_STATE_KEYS = [
  'mobile',
  'tablet',
  'desktop',
  'sm',
  'md',
  'lg',
  'xlg',
  'pointerFine',
] as const

export type MediaStateKey = (typeof MEDIA_STATE_KEYS)[number]
export type MediaState = Record<MediaStateKey, boolean>

export const MEDIA_QUERIES = {
  mobile: '(max-width: 767px)',
  tablet: '(min-width: 768px) and (max-width: 1023px)',
  desktop: '(min-width: 1024px)',
  sm: '(min-width: 768px)',
  md: '(min-width: 1024px)',
  lg: '(min-width: 1260px)',
  xlg: '(min-width: 1440px)',
  pointerFine: '(pointer: fine)',
} as const satisfies Record<MediaStateKey, string>

export const SERVER_MEDIA_STATE: MediaState = {
  mobile: false,
  tablet: false,
  desktop: false,
  sm: false,
  md: false,
  lg: false,
  xlg: false,
  pointerFine: false,
}

type MatchMedia = (query: string) => MediaQueryList

export type MediaStateStore = {
  subscribe: (listener: () => void) => () => void
  getSnapshot: () => MediaState
  getServerSnapshot: () => MediaState
  getKeySnapshot: (key: MediaStateKey) => () => boolean
  getServerKeySnapshot: (key: MediaStateKey) => () => boolean
  getClientKeySnapshot: (key: MediaStateKey) => () => boolean | undefined
  getServerClientKeySnapshot: () => undefined
}

function isSameMediaState(a: MediaState, b: MediaState) {
  return MEDIA_STATE_KEYS.every(key => a[key] === b[key])
}

function cached<T>(cache: Map<MediaStateKey, T>, key: MediaStateKey, create: () => T): T {
  let getter = cache.get(key)
  if (!getter) {
    getter = create()
    cache.set(key, getter)
  }
  return getter
}

export function createMediaStateStore(resolveMatchMedia: () => MatchMedia | null): MediaStateStore {
  const listeners = new Set<() => void>()
  const keySnapshots = new Map<MediaStateKey, () => boolean>()
  const clientKeySnapshots = new Map<MediaStateKey, () => boolean | undefined>()
  const serverKeySnapshots = new Map<MediaStateKey, () => boolean>()

  let snapshot: MediaState = SERVER_MEDIA_STATE
  let hasRead = false
  let subscriptions: { list: MediaQueryList; onChange: () => void }[] | null = null

  const read = (): MediaState => {
    const matchMedia = resolveMatchMedia()
    if (!matchMedia) return SERVER_MEDIA_STATE

    const state = { ...SERVER_MEDIA_STATE }
    for (const key of MEDIA_STATE_KEYS) {
      state[key] = matchMedia(MEDIA_QUERIES[key]).matches
    }
    return state
  }

  const notify = () => {
    for (const listener of listeners) listener()
  }

  const refresh = ({ force = false } = {}) => {
    const next = read()
    if (!force && isSameMediaState(snapshot, next)) return

    snapshot = next
    notify()
  }

  const getSnapshot = () => {
    if (!hasRead && resolveMatchMedia()) {
      snapshot = read()
      hasRead = true
    }

    return snapshot
  }

  const start = () => {
    const matchMedia = resolveMatchMedia()
    if (!matchMedia || subscriptions) return

    subscriptions = MEDIA_STATE_KEYS.map(key => {
      const list = matchMedia(MEDIA_QUERIES[key])
      const onChange = () => refresh()
      list.addEventListener('change', onChange)
      return { list, onChange }
    })

    const wasRead = hasRead
    hasRead = true
    refresh({ force: !wasRead })
  }

  const stop = () => {
    for (const { list, onChange } of subscriptions ?? []) {
      list.removeEventListener('change', onChange)
    }
    subscriptions = null
  }

  return {
    subscribe(listener) {
      listeners.add(listener)
      start()

      return () => {
        listeners.delete(listener)
        if (listeners.size === 0) stop()
      }
    },
    getSnapshot,
    getServerSnapshot: () => SERVER_MEDIA_STATE,
    getKeySnapshot: key => cached(keySnapshots, key, () => () => getSnapshot()[key]),
    getServerKeySnapshot: key =>
      cached(serverKeySnapshots, key, () => () => SERVER_MEDIA_STATE[key]),
    getClientKeySnapshot: key =>
      cached(clientKeySnapshots, key, () => () => (hasRead ? getSnapshot()[key] : undefined)),
    getServerClientKeySnapshot: () => undefined,
  }
}

export const mediaStateStore = createMediaStateStore(() =>
  typeof window === 'undefined' || typeof window.matchMedia !== 'function'
    ? null
    : query => window.matchMedia(query)
)
